/**
 * What a table is made of, and what somebody has done to it.
 *
 * Two tables read from this — the people in a scenario and the changes to it —
 * and they have nothing in common except that both are a list of rows with
 * columns somebody wants to choose between. So the column *definitions* live
 * with each table and everything about a person's preferences lives here, keyed
 * by table id and kept in the browser.
 */

/** One column: what it is called, and how to get it out of a row. */
export interface ColumnDef<Row> {
  key: string;
  label: string;
  /**
   * The cell, as a value rather than a node.
   *
   * A string or a number, never markup, because the same function feeds the
   * screen, the CSV and the spreadsheet — three renderers that would otherwise
   * disagree about what a column says. `null` is an empty cell.
   */
  value: (row: Row) => string | number | null;
  /** Numbers read right-aligned; everything else does not. */
  align?: 'right';
  /** Off unless somebody turns it on. For the columns most tables do not need. */
  hiddenByDefault?: boolean;
  /**
   * Covered until somebody asks for it, cell by cell.
   *
   * For money. The app is opened in meetings and on shared screens, and a figure
   * that is on the page whether or not anybody meant to show it will eventually
   * be shown to the wrong room — the same reason `SecretAmount` exists on a card.
   *
   * An *export* is not covered: asking for the file is the deliberate act that
   * revealing a cell is, and a spreadsheet of dots would be useless.
   */
  secret?: boolean;
}

/**
 * A person's arrangement of one table.
 *
 * Two lists rather than one of visible columns, and the distinction is the
 * point: a column added to the app after somebody last touched their table is
 * *not* in `order` and *not* in `hidden`, so it appears, at the end. One list
 * of what to show would hide it forever, which is how a new feature becomes
 * invisible to exactly the people who have used the thing longest.
 */
export interface TableView {
  /** Column keys, outermost first. Anything absent sorts after everything here. */
  order: string[];
  /** Column keys somebody has turned off. */
  hidden: string[];
}

export const EMPTY_TABLE_VIEW: TableView = { order: [], hidden: [] };

/**
 * The columns to draw, in the order to draw them.
 *
 * Ordering is stable for the columns nobody has moved: `order` decides the ones
 * it names and the definition order decides the rest, which is what keeps a
 * newly added column next to the one it belongs with rather than first.
 */
export function columnsFor<Row>(
  defs: readonly ColumnDef<Row>[],
  view: TableView = EMPTY_TABLE_VIEW,
): ColumnDef<Row>[] {
  const hidden = new Set(view.hidden);
  const rank = new Map(view.order.map((key, index) => [key, index]));

  return defs
    .filter((def) => !hidden.has(def.key) && (!def.hiddenByDefault || rank.has(def.key)))
    .map((def, index) => ({ def, index }))
    .sort((a, b) => {
      const left = rank.get(a.def.key);
      const right = rank.get(b.def.key);
      if (left !== undefined && right !== undefined) return left - right;
      // Anything somebody has arranged comes before anything they have not.
      if (left !== undefined) return -1;
      if (right !== undefined) return 1;
      return a.index - b.index;
    })
    .map(({ def }) => def);
}

/**
 * Turn a column on or off.
 *
 * Turning one *on* also writes it into `order`, slotted where the table declares
 * it, because a default-off column is not shown at all until it is named there.
 * Turning one *off* leaves `order` alone: writing it there would rank a hidden
 * column ahead of every column nobody has arranged, so switching one off and
 * back on reordered the table around it.
 */
export function toggleColumn<Row>(defs: readonly ColumnDef<Row>[], view: TableView, key: string): TableView {
  /*
   * Asked of the table as it stands, not of `hidden`: a default-off column is
   * absent from both lists, so "is it in `hidden`?" answers the wrong question
   * for exactly the columns this exists to switch on.
   */
  const showing = !columnsFor(defs, view).some((def) => def.key === key);

  const hidden = new Set(view.hidden);
  if (showing) hidden.delete(key);
  else hidden.add(key);

  const order = showing && !view.order.includes(key) ? orderWith(defs, view, key) : view.order;

  return { order, hidden: [...hidden] };
}

/**
 * The columns on screen, plus `key`, where the table declares it belongs.
 *
 * A *complete* list, not an insertion into `order` — which is often empty, and
 * inserting into an empty list produced an arrangement naming one column, which
 * then ranked ahead of every column nobody had arranged. So switching a column
 * off and back on moved it to the front.
 */
function orderWith<Row>(defs: readonly ColumnDef<Row>[], view: TableView, key: string): string[] {
  const declared = new Map(defs.map((def, index) => [def.key, index]));
  const target = declared.get(key) ?? defs.length;

  const shown = columnsFor(defs, view).map((def) => def.key);
  const at = shown.findIndex((existing) => (declared.get(existing) ?? -1) > target);

  const next = [...shown];
  if (at === -1) next.push(key);
  else next.splice(at, 0, key);
  return next;
}

/**
 * Move a column one place, in the order somebody actually sees.
 *
 * Written against the visible list rather than against `order`, because `order`
 * may not mention the neighbour yet — the first drag of a column that has never
 * been moved has to work, and it has to swap with what is next to it on screen.
 */
export function moveColumn<Row>(
  defs: readonly ColumnDef<Row>[],
  view: TableView,
  key: string,
  direction: -1 | 1,
): TableView {
  const shown = columnsFor(defs, view).map((def) => def.key);
  const from = shown.indexOf(key);
  const to = from + direction;
  if (from === -1 || to < 0 || to >= shown.length) return view;

  const next = [...shown];
  const [moved] = next.splice(from, 1);
  if (moved !== undefined) next.splice(to, 0, moved);
  return { ...view, order: next };
}

/**
 * Put one column where another one is, which is what a drag means.
 *
 * Written against the visible order for the same reason `moveColumn` is: the
 * arrangement somebody sees is the one they are rearranging, and `order` may
 * not mention either column yet.
 */
export function moveColumnTo<Row>(
  defs: readonly ColumnDef<Row>[],
  view: TableView,
  key: string,
  onto: string,
): TableView {
  const shown = columnsFor(defs, view).map((def) => def.key);
  const from = shown.indexOf(key);
  const to = shown.indexOf(onto);
  if (from === -1 || to === -1 || from === to) return view;

  const next = [...shown];
  const [moved] = next.splice(from, 1);
  if (moved !== undefined) next.splice(to, 0, moved);
  return { ...view, order: next };
}

/** Every column, in the order the picker should list them. */
export function pickerRows<Row>(
  defs: readonly ColumnDef<Row>[],
  view: TableView,
): { def: ColumnDef<Row>; shown: boolean }[] {
  const shown = columnsFor(defs, view);
  const shownKeys = new Set(shown.map((def) => def.key));
  const rest = defs.filter((def) => !shownKeys.has(def.key));
  return [...shown.map((def) => ({ def, shown: true })), ...rest.map((def) => ({ def, shown: false }))];
}

/** The rows a table is showing, as a grid: a header row and one row per record. */
export function tableGrid<Row>(defs: readonly ColumnDef<Row>[], rows: readonly Row[]): string[][] {
  return [defs.map((def) => def.label), ...rows.map((row) => defs.map((def) => stringify(def.value(row))))];
}

function stringify(value: string | number | null): string {
  if (value === null) return '';
  return typeof value === 'number' ? String(value) : value;
}

/**
 * A grid as CSV, RFC 4180 style.
 *
 * CRLF and quoted fields, because the thing on the other end of this is Excel
 * far more often than it is a script: a bare newline inside a note turns one row
 * into two there, and a comma in a title turns one column into two.
 */
export function toCsv(grid: readonly string[][]): string {
  return grid.map((row) => row.map(csvCell).join(',')).join('\r\n');
}

function csvCell(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replaceAll('"', '""')}"` : value;
}
